import { AppType } from "client/Api/Apps/common";
import {
	BirthdayScopeKind,
	EditBirthdayAppViewModel,
	saveBirthdayApp,
	SaveBirthdayAppModel
} from "client/Api/Apps/EditApp/saveApp";
import {
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError
} from "client/Components";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { EditAppSavedResult, useSaveApp } from "../useSaveApp";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { setTrackingId } from "client/Misc/utils";

export type EditBirthdayAppProps = {
	initialSettings: EditAppSettings<EditBirthdayAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditBirthdayApp = forwardRef(
	(
		props: EditBirthdayAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveBirthdayAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveBirthdayAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveBirthdayAppModel>(
			formHandler,
			saveBirthdayApp,
			AppType.Birthday
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under(
				"scope"
			)
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem
					label={localize("_ShowBirthdaysFor")}
					isForGroup
				>
					<RadioButtonGroup
						{...formHandler.for("scope")
							.attachToRadioButtonGroup()
						}
						options={showBirthdaysForOptions}
					/>
					<ValidationError {...formHandler.for("scope").attachToValidationError()} />
					<FormItem.Description
						{...setTrackingId("show-birthdays-for-info")}
					>
						{localize("_BirthdaysVisibilityAppliesToTileAndActivities")}
					</FormItem.Description>
				</FormItem>
			</EditAppBase>
		);
	}
);
EditBirthdayApp.displayName = "EditBirthdayApp";

const validation = createValidationConfiguration<SaveBirthdayAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("scope")
	.add(
		isNotNullNorUndefined()
	);

const showBirthdaysForOptions: readonly RadioButtonGroupItem<BirthdayScopeKind>[] = [
	{
		value: BirthdayScopeKind.Everyone,
		text: localize("_EveryoneInTheOrganisation")
	},
	{
		value: BirthdayScopeKind.Department,
		text: localize("_PeopleInTheSameDepartment")
	}
];

function convertToSaveModel(
	initialSettings: EditAppSettings<EditBirthdayAppViewModel>
): SaveBirthdayAppModel {
	const {
		scope
	} = initialSettings.model.app;

	return {
		...initialGenericState(initialSettings),
		scope
	};
}
